/**
 * 이상한 방 - 열린 방 목록 (M14, 순수 DOM)
 *
 * 근거 문서: 로드맵 M14(`roomBrowser.ts` - 줄 하나 = 방장 이름 · 인원 · 설정 요약. 규칙 1) ·
 *            `ui/lobby.ts` 머리말(계산하지 않는다 · 이름은 한 번 적는다)
 *
 * ## 계산하지 않는다
 *
 * 목록에 실릴 조건 셋(공개 · 로비 · 빈 자리)은 **서버가 이미 판단했다**(M14 규칙 1). 여기서 `players < seatsMax`를
 * 다시 세면 그것이 서버 판단의 사본이고, 그 사본은 언제나 한 박자 낡는다 - 사람은 "들어갈 수 있다고 써 있었는데
 * 꽉 찼다"를 보게 된다. 화면은 받은 줄을 그리고 누른 것을 알릴 뿐이다.
 *
 * ## 이름 칸이 여기 있는 이유
 *
 * 줄을 누르면 **곧바로** 그 방에 앉는다. 이름을 폼에서 받아 오게 하면 목록 → 폼 → 방으로 화면이 셋이 되고,
 * 처음 온 사람은 방을 고른 뒤에 되돌려 보내진다. 다듬기는 로비와 **같은 함수**다(`tidyNameInput` - 빈 이름은
 * 기본 이름으로, 길이는 서버가 자르는 값으로).
 *
 * ## 비어 있음과 닿지 않음은 다른 말이다
 *
 * "지금 열린 방이 없다"와 "서버에 닿지 않는다"는 다음 행동이 다르다 - 앞은 [새 방 열기]이고 뒤는 [다시 보기]다.
 * 한 문장으로 묶으면 서버가 죽은 밤에 사람들이 계속 새 방을 열려고 한다.
 *
 * 문구는 전부 사전이다. 언어가 바뀌면 굳은 라벨을 다시 칠하고 마지막 상태를 다시 그린다(`ui/lobby.ts`와 같은 결정).
 */

import type { Verb } from '../core/types'
import { onLocaleChange, t } from '../i18n'
import { MAX_PLAYER_NAME_LENGTH, type RoomListing } from '../net/protocol'

import { createElement, setAttr, setDisabled, setHidden, setText } from './dom'
import { tidyNameInput } from './lobby'
import './menu.css'
import './roomBrowser.css'

/* --- 타입 ---------------------------------------------------------------------- */

/** 목록이 지금 무엇을 말하고 있는가. `loading`은 첫 답을 기다리는 동안이다. */
export type RoomBrowserState = 'loading' | 'list' | 'offline'

export type RoomBrowserView = Readonly<{
  state: RoomBrowserState
  /** `state`가 `list`일 때의 줄들. 비어 있으면 "지금 열린 방이 없다"다. */
  rooms: readonly RoomListing[]
  /** 이름 칸의 기본값(`netBoot.rememberedName`). */
  name: string
}>

export type RoomBrowserHandlers = Readonly<{
  /** 줄을 눌렀다. 번호로 들어가는 것과 **같은 길**이다(M14 규칙 2). */
  onJoin: (code: string, name: string) => void
  /** 공개 방을 새로 연다. */
  onCreate: (name: string) => void
  onRefresh: () => void
  onBack: () => void
}>

export type RoomBrowserHandle = Readonly<{
  show: (view: RoomBrowserView) => void
  hide: () => void
  isVisible: () => boolean
  dispose: () => void
}>

export type RoomBrowserOptions = Readonly<{ mount: HTMLElement; handlers: RoomBrowserHandlers }>

/* --- 줄 문구 -------------------------------------------------------------------- */

/**
 * 줄 아래 설정 요약. **동사와 개수 둘뿐이다** - 그 둘이 방장이 로비에서 정할 수 있는 전부이고(`LobbySettingsView`),
 * 목록에 그 밖의 것을 적으면 방에 들어가야 알 수 있는 값을 화면이 아는 척하게 된다.
 */
export function roomSummary(room: RoomListing): readonly string[] {
  const verbs = room.allowedVerbs.map((verb: Verb) => t(`verb.${verb}`)).join(' ')
  return [t('party.browse.chipOddities', { count: room.changeCount }), verbs].filter((text) => text !== '')
}

/* --- 본체 ---------------------------------------------------------------------- */

export function createRoomBrowser(options: RoomBrowserOptions): RoomBrowserHandle {
  const { handlers } = options

  const root = createElement('div', 'rb-root')
  root.hidden = true
  root.setAttribute('role', 'dialog')
  root.setAttribute('aria-modal', 'true')
  root.setAttribute('aria-labelledby', 'rb-title')

  const head = createElement('header', 'mn-head')
  const eyebrow = createElement('p', 'mn-eyebrow', '')
  const title = createElement('h1', 'mn-title', '')
  title.id = 'rb-title'
  head.append(eyebrow, title)

  /* --- 이름 --- */
  const nameField = createElement('label', 'rb-field')
  const nameLabel = createElement('span', 'rb-label', '')
  const nameInput = createElement('input', 'rb-input')
  nameInput.type = 'text'
  nameInput.maxLength = MAX_PLAYER_NAME_LENGTH
  setAttr(nameInput, 'autocomplete', 'nickname')
  nameField.append(nameLabel, nameInput)

  /* --- 목록 --- */
  const list = createElement('ul', 'rb-list')
  const note = createElement('p', 'rb-note', '')

  /* --- 아래 버튼 줄 --- */
  const actions = createElement('div', 'rb-actions')
  const createButton = createElement('button', 'rb-primary', '') as HTMLButtonElement
  createButton.type = 'button'
  const refreshButton = createElement('button', 'rb-secondary', '') as HTMLButtonElement
  refreshButton.type = 'button'
  actions.append(createButton, refreshButton)

  const back = createElement('button', 'mn-back', '') as HTMLButtonElement
  back.type = 'button'

  root.append(head, nameField, list, note, actions, back)
  options.mount.append(root)

  /** 마지막으로 그린 것. 언어가 바뀌면 이것으로 다시 그린다. */
  let last: RoomBrowserView | null = null

  /* --- 손 --- */

  function name(): string {
    return tidyNameInput(nameInput.value)
  }

  createButton.addEventListener('click', () => handlers.onCreate(name()))
  refreshButton.addEventListener('click', () => handlers.onRefresh())
  back.addEventListener('click', () => handlers.onBack())

  /* --- 칠하기 --- */

  function paintStatic(): void {
    setText(eyebrow, t('party.card.eyebrow'))
    setText(title, t('party.browse.title'))
    setText(nameLabel, t('lobby.name.label'))
    setText(createButton, t('party.browse.create'))
    setText(refreshButton, t('party.browse.refresh'))
    setText(back, t('menu.back'))
  }

  function rowOf(room: RoomListing): HTMLLIElement {
    const item = createElement('li', 'rb-item')
    const button = createElement('button', 'rb-row') as HTMLButtonElement
    button.type = 'button'

    const seats = t('party.browse.seats', { players: room.players, max: room.seatsMax })
    button.setAttribute('aria-label', t('party.browse.rowAria', { host: room.host, seats }))

    const body = createElement('span', 'rb-row__body')
    body.setAttribute('aria-hidden', 'true')
    body.append(createElement('span', 'rb-row__host', room.host))
    const meta = createElement('span', 'mn-card__meta')
    for (const text of roomSummary(room)) meta.append(createElement('span', 'mn-chip', text))
    body.append(meta)

    const count = createElement('span', 'rb-row__seats', seats)
    count.setAttribute('aria-hidden', 'true')

    button.append(body, count)
    button.addEventListener('click', () => handlers.onJoin(room.code, name()))
    item.append(button)
    return item
  }

  function paint(view: RoomBrowserView): void {
    // 입력 칸은 사람이 적는 중일 수 있다 - 값이 왔을 때만 덮는다(`ui/lobby.paintForm`과 같은 규약).
    if (view.name !== '' && nameInput.value === '') nameInput.value = view.name

    const rooms = view.state === 'list' ? view.rooms : []
    list.replaceChildren(...rooms.map(rowOf))
    setHidden(list, rooms.length === 0)

    /*
     * 셋을 다르게 말한다 - 기다리는 중 · 방이 없다 · 닿지 않는다. 마지막 둘은 다음 행동이 다르므로
     * 한 문장으로 묶지 않는다(머리말). 그래서 [새 방 열기]도 닿지 않을 때는 잠긴다.
     */
    const noteKey =
      view.state === 'loading'
        ? 'party.browse.loading'
        : view.state === 'offline'
          ? 'net.error.offline'
          : rooms.length === 0
            ? 'party.browse.empty'
            : null
    setHidden(note, noteKey === null)
    if (noteKey !== null) setText(note, t(noteKey))
    setDisabled(createButton, view.state === 'offline')
  }

  paintStatic()

  const stopLocaleWatch = onLocaleChange(() => {
    paintStatic()
    if (last !== null) paint(last)
  })

  return {
    show(view): void {
      last = view
      paint(view)
      root.hidden = false
    },

    hide(): void {
      if (root.contains(document.activeElement)) {
        const focused = document.activeElement
        if (focused instanceof HTMLElement) focused.blur()
      }
      root.hidden = true
    },

    isVisible: () => !root.hidden,

    dispose(): void {
      stopLocaleWatch()
      root.remove()
    },
  }
}
